import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Box } from '@mui/material';
import classNames from 'classnames';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { useFormikContext } from 'formik';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { useCurrentUserClientTimezone } from '../../../../../../../contexts/OidcContext/OidcContext.selectors';
import { useRmsGanttValue } from '../../../../RmsContext';
import { ganttOneMinuteInMilliSeconds } from '../../../gantt.constants';
import { TurnaroundFormValues } from '../TurnaroundForm.utils';
import { StyledButtonsBox, StyledSquareIconButton, StyledTimeField } from './BoardingGateInputField.style';

export const BoardingGateInputField = memo(function BoardingGateInputField({
    field,
    updateField,
    disabled,
}: {
    field: string;
    updateField: string;
    disabled: boolean;
}) {
    const { values, setValues, errors } = useFormikContext<TurnaroundFormValues>();
    const turnaround = useRmsGanttValue('selectedTurnarounds')?.[0];
    const timerRef = useRef<NodeJS.Timeout>();

    //minimum opening boarding time
    const delta = 20;

    const maxDate = useMemo(() => {
        if (field === 'actualBoardingStart' || field === 'actualBoardingEnd') {
            return new Date();
        } else return null;
    }, [field]);

    const timezone = useCurrentUserClientTimezone();

    const handleFocus = useCallback(() => {
        setValues({
            ...values,
            [updateField]: true,
        });
    }, [setValues, updateField, values]);

    const handleChange = useCallback(
        (newValue: number | Date) => {
            setValues({
                ...values,
                [updateField]: true,
                [field]: newValue,
            });
        },
        [field, setValues, updateField, values],
    );

    const updateFieldValue = useCallback(
        (add: boolean, value: number) => {
            const newDate = add ? value + ganttOneMinuteInMilliSeconds : value - ganttOneMinuteInMilliSeconds;
            setValues({
                ...values,
                [updateField]: true,
                [field]: newDate,
            });
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => updateFieldValue(add, newDate), 200);
        },
        [field, setValues, updateField, values],
    );

    const handleMouseDown = useCallback(
        (event, add: boolean) => {
            updateFieldValue(add, values[field]);
        },
        [field, updateFieldValue, values],
    );

    const handleMouseUp = useCallback(() => {
        clearTimeout(timerRef.current);
    }, []);

    useEffect(() => {
        return () => {
            clearTimeout(timerRef.current);
        };
    }, []);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <StyledTimeField
                label={<FormattedMessage id={`turnaround.popin.${field}`} />}
                labelProps={{ style: { color: 'black' } }}
                name={field}
                value={values[field] || new Date()}
                date={values[field]}
                disabled={!values[field] || disabled}
                error={!!errors[field]}
                helperText={
                    typeof errors[field] === 'string' && <FormattedMessage id={errors[field]} values={{ delta }} />
                }
                inputProps={{
                    max: maxDate ? format(utcToZonedTime(maxDate, timezone || ''), 'HH:mm') : undefined,
                }}
                required
                onChange={newValue => handleChange(newValue)}
                onFocus={handleFocus}
                className={classNames({
                    textInGray: !(
                        field === 'actualBoardingStart' ||
                        field === 'actualBoardingEnd' ||
                        values[updateField] ||
                        (turnaround?.departureFlight?.tsbt && turnaround?.departureFlight?.tebt)
                    ),
                })}
            />
            &ensp;
            <StyledButtonsBox>
                <StyledSquareIconButton
                    sx={{ marginBottom: '7px' }}
                    onMouseDown={e => handleMouseDown(e, true)}
                    onMouseUp={handleMouseUp}
                    disabled={
                        !values[field] ||
                        (maxDate &&
                            new Date(values[field]).getHours() >= maxDate.getHours() &&
                            new Date(values[field]).getMinutes() >= maxDate.getMinutes()) ||
                        disabled
                    }
                >
                    <AddIcon sx={{ color: 'white', width: '15px', height: '15px' }} />
                </StyledSquareIconButton>
                <StyledSquareIconButton
                    onMouseDown={e => handleMouseDown(e, false)}
                    onMouseUp={handleMouseUp}
                    disabled={!values[field] || disabled}
                >
                    <RemoveIcon sx={{ color: 'white', width: '15px', height: '15px' }} />
                </StyledSquareIconButton>
            </StyledButtonsBox>
        </Box>
    );
});
